import { css } from '@emotion/react';
import { SegmentedControlSize, SegmentedControlWidth } from './types';
import { semantic_colors, shapes, text_styles } from '../../foundation';

interface SegmentedControlStyleProps {
  size: SegmentedControlSize;
  disabled?: boolean;
  width?: SegmentedControlWidth;
}

interface SegmentedControlOptionStyleProps {
  size: SegmentedControlSize;
  checked?: boolean;
  disabled?: boolean;
  width?: SegmentedControlWidth;
  focused?: boolean;
  thumbShow?: boolean;
}

const colors = {
  background: semantic_colors.fill.secondary,
  backgroundDisabled: semantic_colors.fill.disabled,
  backgroundChecked: semantic_colors.background.surface,
};

const SIZE_INFO: Record<
  SegmentedControlSize,
  { icon: number; itemHeight: number; itemPadding: string; text_style: string; wrapperMargin: number }
> = {
  large: {
    icon: 16,
    itemHeight: 38,
    itemPadding: '7px 14px',
    text_style: text_styles.Body_14_Medium,
    wrapperMargin: 4,
  },
  medium: {
    icon: 14,
    itemHeight: 30,
    itemPadding: '4px 10px',
    text_style: text_styles.Body_13_Medium,
    wrapperMargin: 4,
  },
  xsmall: { icon: 12, itemHeight: 22, itemPadding: '0 8px', text_style: text_styles.Body_13_Medium, wrapperMargin: 3 },
};

export const getSegmentedControlStyleByProps = ({ width, disabled }: SegmentedControlStyleProps) => {
  const width_style =
    width === 'fill' &&
    css`
      display: flex;
      width: 100%;
    `;
  return css`
    display: inline-block;
    background-color: ${disabled ? colors.backgroundDisabled : colors.background};
    align-self: flex-start;
    overflow: hidden;
    vertical-align: middle;
    ${shapes.border_radius.small};
    ${width_style};
  `;
};

export const getSegmentedControlGroupStyleByProps = ({ size }: SegmentedControlStyleProps) => css`
  position: relative;
  display: flex;
  align-items: stretch;
  justify-items: flex-start;
  flex-direction: row;
  width: calc(100% - ${SIZE_INFO[size].wrapperMargin * 2}px);
  margin: ${SIZE_INFO[size].wrapperMargin}px;
`;

export const getSegmentedControlOptionStyleByProps = ({
  width,
  focused,
  disabled,
  checked,
  thumbShow,
}: SegmentedControlOptionStyleProps) => {
  const width_style =
    width === 'fill' &&
    css`
      flex: 1;
      min-width: 0;
    `;
  const focused_style =
    focused &&
    css`
      outline: 3px solid ${semantic_colors.tab_focus};
    `;
  const state_style =
    !disabled &&
    css`
      &:hover:not(:disabled),
      &:active:not(:disabled) {
        color: ${semantic_colors.content.primary};
      }
    `;

  const checked_style =
    checked &&
    !thumbShow &&
    css`
      background-color: ${colors.backgroundChecked};
      ${disabled &&
      css`
        opacity: 0.4;
      `};
    `;

  return css`
    position: relative;
    transition: color 0.1s linear, background-color 0.1s linear;
    transform: translateZ(0);
    border-radius: 6px;
    color: ${checked
      ? semantic_colors.content.primary
      : disabled
      ? semantic_colors.content.disabled
      : semantic_colors.content.secondary};

    cursor: ${disabled ? 'auto' : 'pointer'};
    ${width_style}
    ${state_style}
    ${focused_style}
    ${checked_style}

    &:focus-visible {
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
    }
  `;
};

export const getSegmentedControlOptionLabelStyleBySize = (size: SegmentedControlSize) => css`
  ${SIZE_INFO[size].text_style};
  height: ${SIZE_INFO[size].itemHeight}px;
  padding: ${SIZE_INFO[size].itemPadding};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

export const getIconSizeBySize = (size: SegmentedControlSize) => {
  return SIZE_INFO[size].icon;
};

export const getSegmentedControlOptionIconStyleBySize = (size: SegmentedControlSize) => css`
  font-size: ${SIZE_INFO[size].icon}px;
  width: ${SIZE_INFO[size].icon}px;
  height: ${SIZE_INFO[size].icon}px;
  color: inherit;
  line-height: 0;
  display: inline-block;
  flex: 1 0 1;
`;

export const segmented_option_text_style = css`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const segment_thumb_style = css`
  position: absolute;
  border-radius: 6px;
  background-color: ${colors.backgroundChecked};
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.1);
  top: 0;
  height: 100%;
`;
